import { useEnterpriseMobileAppSettingsQuery } from 'types/graphql';
import { useStores } from 'src/hooks/use-stores';

type UseEnterpriseHasMobileAppReturn = {
  hasMobileApp: boolean;
  loading: boolean;
};

/**
 * Same check `src/utils/use-has-mobile-app.ts` does (an iOS bundle identifier or Android package
 * name configured), but keyed directly off an `enterpriseId` rather than `UI.dispensary` (mobx) —
 * that hook needs a *loaded* dispensary, which the enterprise Mobile App tab's location picker
 * doesn't have yet (nothing's been picked). Mobile app enablement is an enterprise-wide setting,
 * not per-location, so this is the same answer for every location in the enterprise.
 */
export function useEnterpriseHasMobileApp(enterpriseId: string | undefined): UseEnterpriseHasMobileAppReturn {
  const { apolloClient } = useStores();

  const { data, loading } = useEnterpriseMobileAppSettingsQuery({
    client: apolloClient,
    skip: !enterpriseId,
    variables: { enterpriseId: enterpriseId ?? '' },
  });

  // Use `||`, not `??`: an Android-only (or iOS-only) app can have the other identifier come
  // back as `''` rather than `null`, and `??` only falls through on null/undefined -- it would
  // stop at the empty string and never consult the field that's actually set.
  const hasMobileApp = Boolean(
    data?.enterpriseMobileAppSettings?.iosBundleIdentifier || data?.enterpriseMobileAppSettings?.androidPackageName
  );

  return {
    hasMobileApp,
    loading,
  };
}
